import { AboutCmsContent } from '../models/types';

export const ABOUT_CMS_IDENTIFIER = 'about-page';

export const DEFAULT_ABOUT_CMS: AboutCmsContent = {
  badge: 'About DEX',
  title: 'The Future of',
  titleHighlight: 'Decentralized Finance',
  subtitle:
    'The next generation cross-chain decentralized exchange. Establish secure compounding liquidity yields within a luxury DeFi system.',
  mission: {
    title: 'Our Mission',
    paragraph1:
      'DEX is a next-generation cross-chain decentralized exchange built to make DeFi accessible, secure, and rewarding for everyone — from first-time swappers to seasoned liquidity providers.',
    paragraph2:
      'We combine audited smart contracts, intuitive design, and deep liquidity to deliver a premium trading experience across swap, staking, yield farming, and liquidity management.',
  },
  stats: [
    { label: 'Total Value Locked', value: '$168M+', change: '+8.4%' },
    { label: 'Daily Volume', value: '$24.6M', change: '+12.1%' },
    { label: 'Active Wallets', value: '48,200+', change: '+5.8%' },
    { label: 'Supported Chains', value: '3', change: 'ETH · BNB · MATIC' },
  ],
  ecosystem: {
    title: 'The DEX Ecosystem',
    subtitle:
      'A complete suite of DeFi products designed to help you trade, earn, and grow your portfolio.',
    items: [
      { title: 'Swap Core', description: 'Instant asset trading across multiple networks.' },
      { title: 'Liquidity', description: 'Contribute LP capital and capture trade fee commissions.' },
      { title: 'Yield Farms', description: 'Stake LP tokens to harvest compounding high APR yields.' },
      { title: 'Token Staking', description: 'Stake tokens directly to earn ecosystem revenues.' },
    ],
  },
  values: {
    title: 'Why Choose DEX',
    items: [
      {
        title: 'Security First',
        description:
          'Every core contract is audited and monitored. Your assets stay in self-custody while you trade, stake, and farm.',
      },
      {
        title: 'Cross-Chain by Design',
        description:
          'Swap and provide liquidity across Ethereum, BNB Chain, and Polygon from a single luxury DeFi interface.',
      },
      {
        title: 'Community Driven',
        description:
          'DEX token holders shape protocol upgrades, fee structures, and ecosystem incentives through on-chain governance.',
      },
      {
        title: 'Built for Performance',
        description:
          'Optimized routing and low-latency execution deliver institutional-grade speed without sacrificing decentralization.',
      },
    ],
  },
  trust: {
    title: 'Built for everyone. Secured by the best.',
    items: [
      {
        title: 'Fully Audited',
        description: 'Smart contracts audited by leading security agencies CertiK & PeckShield.',
      },
      {
        title: 'Lowest Payout Fees',
        description: 'Highly optimized smart contracts ensure minimal gas fees for swap actions.',
      },
      {
        title: 'Instant Execution',
        description: 'High performance blockchain infrastructure triggers transactions instantly.',
      },
      {
        title: 'Community Governance',
        description: 'Platform updates are voted on and executed by DEX token holders.',
      },
    ],
  },
  cta: {
    title: 'Ready to get started?',
    description:
      'Explore swaps, liquidity pools, and staking — or reach out to our team with any questions.',
    primaryButton: 'Start Trading',
    secondaryButton: 'Contact Us',
  },
  seo: {
    metaTitle: 'About Us | DEX',
    metaDescription:
      'Learn about DEX — the cross-chain decentralized exchange built for secure trading, staking, and yield farming.',
    keywords: 'dex, about, defi, decentralized exchange, cross-chain',
    ogImage: '',
  },
};

function sanitizeText(val: any): any {
  if (typeof val === 'string') {
    return val
      .replace(/DEXOR\s*AI/gi, 'DEX')
      .replace(/STAKE\s+DXOR/gi, 'Stake Tokens')
      .replace(/STAKE\s+DEXOR/gi, 'Stake Tokens')
      .replace(/DXOR\s+staking/gi, 'token staking')
      .replace(/DEXOR\s+staking/gi, 'token staking')
      .replace(/\bDXOR\b/g, 'DEX')
      .replace(/\bDexor\b/g, 'DEX')
      .replace(/\bDEXOR\b/g, 'DEX');
  }
  if (Array.isArray(val)) {
    return val.map(sanitizeText);
  }
  if (val !== null && typeof val === 'object') {
    const res: any = {};
    for (const key of Object.keys(val)) {
      res[key] = sanitizeText(val[key]);
    }
    return res;
  }
  return val;
}

export const parseAboutCmsContent = (content?: string): AboutCmsContent => {
  if (!content) return DEFAULT_ABOUT_CMS;

  try {
    const rawParsed = typeof content === 'string' ? JSON.parse(content) as Partial<AboutCmsContent> : content as Partial<AboutCmsContent>;
    const parsed = sanitizeText(rawParsed) as Partial<AboutCmsContent>;
    const fallback = DEFAULT_ABOUT_CMS;
    return {
      ...fallback,
      ...parsed,
      mission: { ...fallback.mission, ...parsed.mission },
      ecosystem: {
        ...fallback.ecosystem,
        ...parsed.ecosystem,
        items: parsed.ecosystem?.items?.length ? parsed.ecosystem.items : fallback.ecosystem.items,
      },
      values: {
        ...fallback.values,
        ...parsed.values,
        items: parsed.values?.items?.length ? parsed.values.items : fallback.values.items,
      },
      trust: {
        ...fallback.trust,
        ...parsed.trust,
        items: parsed.trust?.items?.length ? parsed.trust.items : fallback.trust.items,
      },
      cta: { ...fallback.cta, ...parsed.cta },
      seo: { ...fallback.seo, ...parsed.seo },
      stats: parsed.stats?.length ? parsed.stats : fallback.stats,
    };
  } catch {
    return DEFAULT_ABOUT_CMS;
  }
};
